import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, signOut as firebaseSignOut } from 'firebase/auth';
import api from '../utils/api';
import { storage } from '../utils/storage';
import { auth as firebaseAuth } from '../firebase';
import { User, LoginRequest, RegisterRequest, RegisterCompanyRequest } from '../types/auth';
import toast from 'react-hot-toast';

export type GoogleLoginResult =
  | { isNewUser: false }
  | { isNewUser: true; email: string; name: string; picture?: string };

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithGoogle: (firebaseUser: FirebaseUser) => Promise<GoogleLoginResult>;
  register: (data: RegisterRequest) => Promise<void>;
  registerCompany: (data: RegisterCompanyRequest) => Promise<void>;
  registerWithGoogle: (idToken: string, inviteCode: string) => Promise<void>;
  registerCompanyWithGoogle: (idToken: string, companyName: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * 토큰 저장 후 /auth/me로 최신 유저 데이터를 가져와 상태 설정.
   * 모든 로그인/가입 메서드에서 공통으로 사용.
   */
  const saveTokenAndFetchUser = async (userToken: string, welcomeName?: string) => {
    // 1. 토큰 저장
    setToken(userToken);
    storage.set('azocsii-token', userToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;

    // 2. /auth/me로 fresh 데이터 가져오기
    try {
      const meResponse = await api.get('/auth/me');
      const freshUser = meResponse.data.success
        ? meResponse.data.data.user   // 기존 형식 { success, data: { user } }
        : meResponse.data.user;       // 새 형식 { user }

      if (freshUser) {
        setUser(freshUser);
        storage.set('azocsii-user', JSON.stringify(freshUser));
        if (welcomeName) {
          toast.success(`환영합니다, ${welcomeName}님! 🧡`);
        }
        return;
      }
    } catch (meError) {
      console.error('/auth/me fetch failed after login:', meError);
    }

    // /auth/me 실패 시 — 토큰은 저장됐으므로 다음 새로고침에서 재시도됨
    // 일단 빈 유저로 설정하지 않고 경고만 (isAuthenticated는 token 존재로 판단)
  };

  // 초기 로드: localStorage에서 토큰 확인 후 최신 정보 가져오기
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = storage.get('azocsii-token');

      if (storedToken) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await api.get('/auth/me');

          const userData = response.data.success
            ? response.data.data.user
            : response.data.user;

          if (userData) {
            setUser(userData);
            setToken(storedToken);
            storage.set('azocsii-user', JSON.stringify(userData));
          }
        } catch (error: any) {
          console.error('Token validation failed:', error);
          if (error.response?.status === 401) {
            storage.remove('azocsii-token');
            storage.remove('azocsii-user');
            setUser(null);
            setToken(null);
          } else {
            const storedUser = storage.get('azocsii-user');
            if (storedUser) {
              setUser(JSON.parse(storedUser));
              setToken(storedToken);
            }
          }
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // 이메일 로그인
  const login = async (credentials: LoginRequest) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const data = response.data;
      const userToken = data.success ? data.data.token : data.token;
      const userName = data.success ? data.data.user.name : data.user?.name;

      await saveTokenAndFetchUser(userToken, userName);
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || '로그인에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // Google 로그인
  const loginWithGoogle = async (firebaseUser: FirebaseUser): Promise<GoogleLoginResult> => {
    try {
      const idToken = await firebaseUser.getIdToken(true);

      let response: any;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          response = await api.post('/auth/google', { idToken });
          break;
        } catch (err: any) {
          const isNetworkError = !err.response;
          if (isNetworkError && attempt < 2) {
            toast(`재시도 ${attempt + 1}/3...`, { icon: '🔄', duration: 3000 });
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }
          throw err;
        }
      }

      const { token: userToken, user: userData } = response!.data;
      await saveTokenAndFetchUser(userToken, userData.name);
      return { isNewUser: false };
    } catch (error: any) {
      await firebaseSignOut(firebaseAuth).catch(() => {});

      if (
        error.response?.status === 404 &&
        error.response?.data?.error === 'NOT_REGISTERED'
      ) {
        toast('먼저 회사를 등록하거나 초대코드로 팀원 가입을 해주세요', { icon: 'ℹ️' });
        return {
          isNewUser: true,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || '',
          picture: firebaseUser.photoURL || undefined,
        };
      }

      const debugInfo = [
        `msg: ${error.message}`,
        `code: ${error.code || 'none'}`,
        `status: ${error.response?.status || 'no response'}`,
        `url: ${error.config?.url || 'unknown'}`,
        `baseURL: ${error.config?.baseURL || 'unknown'}`,
      ].join('\n');
      console.error('[Auth Debug]', debugInfo);
      toast.error(`디버그: ${debugInfo}`, { duration: 15000 });
      throw error;
    }
  };

  // 이메일 팀원 가입 (초대 코드)
  const register = async (data: RegisterRequest) => {
    try {
      const response = await api.post('/auth/register', data);
      const respData = response.data;
      const userToken = respData.success ? respData.data.token : respData.token;
      const userName = respData.success ? respData.data.user.name : respData.user?.name;

      await saveTokenAndFetchUser(userToken, userName);
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || '회원가입에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // 이메일 회사 생성
  const registerCompany = async (data: RegisterCompanyRequest) => {
    try {
      const response = await api.post('/auth/register/company', data);
      const respData = response.data;
      const userToken = respData.success ? respData.data.token : respData.token;
      const inviteCode = respData.success ? respData.data.company?.inviteCode : '';

      await saveTokenAndFetchUser(userToken);
      toast.success(`회사 생성 완료! 초대 코드: ${inviteCode} 🎉`);
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || '회사 생성에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // Google 팀원 가입
  const registerWithGoogle = async (idToken: string, inviteCode: string) => {
    try {
      const response = await api.post('/auth/register/google', { idToken, inviteCode });
      const respData = response.data;
      const userToken = respData.success ? respData.data.token : respData.token;

      await saveTokenAndFetchUser(userToken);
      toast.success('팀원 가입 완료! 🎉');
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Google 회원가입에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // Google 회사 생성
  const registerCompanyWithGoogle = async (idToken: string, companyName: string) => {
    try {
      const response = await api.post('/auth/register/company/google', { idToken, companyName });
      const payload = response.data.data || response.data;
      const userToken = payload.token;
      const inviteCode = payload.company?.inviteCode || '';

      await saveTokenAndFetchUser(userToken);
      toast.success(`회사 생성 완료! 초대 코드: ${inviteCode} 🎉`);
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Google 회사 생성에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // 로그아웃
  const logout = () => {
    setUser(null);
    setToken(null);
    storage.remove('azocsii-token');
    storage.remove('azocsii-user');
    delete api.defaults.headers.common['Authorization'];
    firebaseSignOut(firebaseAuth).catch(() => {});
    toast.success('로그아웃 되었습니다.');
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    loginWithGoogle,
    register,
    registerCompany,
    registerWithGoogle,
    registerCompanyWithGoogle,
    logout,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
